import type React from "react";
import { React, useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import FadeIn from "../components/FadeIn";
import SectionTransition from "../components/SectionTransition";
import {
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { motion } from "framer-motion";

import { Button } from "./ui/button";
import { Input } from "./ui/input";

import logo from "../assets/logo-1.png";
import fssai_no from "../assets/fssai_no.jpg";
import footerBg from "../assets/images/footer-bg.webp";

import PageLoading from "./PageLoading";
import SuccessMessageModal from "./SuccessMessageModal";
import * as AdminActions from "../redux/actions/Admin";
import { encoded } from "../utils";

export default function Footer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const successMessage = useSelector((state) => state.admin.success_message);
  const errorMessage = useSelector((state) => state.admin.error_message);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessMessageModalOpen, setIsSuccessMessageModalOpen] =
    useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");

  const [email_id, setEmailId] = useState("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Newsletter subscription submitted");
    setIsLoading(true);
    let formDataObj = new FormData();
    formDataObj.append(
      "json",
      encoded(
        JSON.stringify({ email_id: email_id, subscriber_id: "", type: "News" })
      )
    );
    dispatch(AdminActions.AddSubscriber(formDataObj));
  };

  useEffect(() => {
    if (successMessage === "AddSubscriber") {
      setIsLoading(false);
      setSuccessMessageText("Successfully Added..");
      setIsSuccessMessageModalOpen(true);
      setEmailId("");
      dispatch(AdminActions.empty_message());
    }
  }, [successMessage, errorMessage, dispatch, navigate]);

  return (
    <footer
      className="text-turmeric-100 relative overflow-hidden"
      style={{
        // backgroundImage: `url(${footerBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
    <div
      className="text-turmeric-100 relative overflow-hidden h-100"
      style={{
        backgroundImage: `url(${footerBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height:"500px"
      }}
    ></div>
    <SectionTransition
            className="py-16 bg-gradient-to-r from-turmeric-700 to-purple-800 text-white relative overflow-hidden"
            delay={0.5}
          >
            <div className="absolute inset-0 bg-kolam-pattern opacity-10"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
    <FadeIn>
      <PageLoading isLoading={isLoading} />
      <SuccessMessageModal
        isOpen={isSuccessMessageModalOpen}
        onClose={() => setIsSuccessMessageModalOpen(false)}
        message={successMessageText}
      />
      <div className="absolute inset-0 to-turmeric-700/80  z-10" />
      <motion.div
        className="container mx-auto px-4 py-12 relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <motion.div className="space-y-4" variants={itemVariants}>
           
            <div>
              <img
                src={fssai_no} // Placeholder for potential logo
                alt="YATRA's COOKING CAVE Logo"
                className="h-15 w-45 object-contain"
              />
            </div>
            <p className="text-white font-bold">
              காயத்ரிபிரியா துரை வழங்கும் வீட்டு சமையலில், தினசரி உணவு மற்றும் சிறப்பு நிகழ்ச்சிகளுக்கும் சுவை மிக்க சிறந்த உணவுகள் முன்பதிவு செய்யுங்கள்.
            </p>
            <div className="flex space-x-4">
              <motion.a
                href="https://www.youtube.com/@YatrasCookingCave"
                className="hover:text-turmeric-800"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Youtube className="h-5 w-5" />
                <span className="sr-only">Youtube</span>
              </motion.a>
              <motion.a
                href="https://facebook.com/yatrascookingcave"
                className="hover:text-turmeric-800"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Facebook className="h-5 w-5" />
                <span className="sr-only">Facebook</span>
              </motion.a>
              <motion.a
                href="https://instagram.com/yatrascookingcave"
                className="hover:text-turmeric-800"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Instagram className="h-5 w-5" />
                <span className="sr-only">Instagram</span>
              </motion.a>
              <motion.a
                href="https://twitter.com/yatrascookingcave"
                className="hover:text-turmeric-800"
                whileHover={{ scale: 1.2, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Twitter className="h-5 w-5" />
                <span className="sr-only">Twitter</span>
              </motion.a>
            </div>
          </motion.div>

          <motion.div className="space-y-4" variants={itemVariants}>
            <h3 className="text-lg font-semibold text-white">
              Quick Links
            </h3>
            <ul className="space-y-2 font-bold text-white">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about-us" },
                { name: "Services", path: "/services" },
                { name: "Who We Are", path: "/who-we-are" },
                { name: "Contact Us", path: "/contact-us" },
                { name: "PreOrder", path: "/pre-order" },
                { name: "Order Now", path: "/order-booking" },
              ].map((link) => (
                <li key={link.path}>
                  <motion.div
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Link
                      to={link.path}
                      className="text-sm text-white hover:text-turmeric-800 hover:underline"
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="space-y-4" variants={itemVariants}>
            <h3 className="text-lg font-semibold text-white">
              Contact Info
            </h3>
            <ul className="space-y-3 font-bold">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 text-white" />
                <span className="text-white-800">
                  கந்தன் குடில் <br></br>#7, அண்ணாநகர் மேற்கு, சென்னை - 600040.
                </span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 mr-2 flex-shrink-0 text-white" />
                <span className="text-white-800">+91 7824099400 </span>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-2 flex-shrink-0 text-white" />
                <span className="text-white-800">
                  yatrascookingcave@gmail.com
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.div className="space-y-4 font-bold" variants={itemVariants}>
            <h3 className="text-lg font-semibold text-white">News Letter</h3>
            <p className="text-white-800">
             Monthly Subscribe for updates on new dishes, offers, and events.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col space-y-2"
            >
              <Input
                type="email"
                placeholder="Your Email address"
                id="email_id"
                value={email_id}
                onChange={(e) => setEmailId(e.target.value)}
                className="bg-turmeric-800 border-turmeric-800 text-white placeholder:text-turmeric-300"
                required
              />
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button className="bg-turmeric-600 hover:bg-turmeric-500 text-turmeric-800 w-full">
                  Subscribe
                </Button>
              </motion.div>
            </form>
          </motion.div>
        </div>

        <motion.div
          className="border-t border-turmeric-800 mt-12 pt-6 text-center text-white-800"
          variants={itemVariants}
        >
          <p>
            © {new Date().getFullYear()} YATRA's COOKING CAVE. All rights
            reserved | Developed By <a href="https://vimkes.com" target="_blank">Vimkes Technology</a>
          </p>
        </motion.div>
      </motion.div>
      </FadeIn>
      </div>
      
      </SectionTransition>
    </footer>
  );
}
